import React, { FC, useState, useEffect, useMemo } from 'react';

import { Center, Flex, Image, Text } from '@chakra-ui/react';

import logo from 'assets/logos/r.World-company-logo.png';
import roundLogo from 'assets/logos/r.World-round-logo.png';

import { routesSettings } from 'constants/routes';

import { washHub } from 'store/slices';
import { useIsSuperAdmin, useRoles } from 'store/slices/auth/authSelectors';
import { RolesEnum } from 'types/user';

import NavItem, { COLLAPSED_RAIL_WIDTH } from './NavItem';

import {
  MdPeople,
  MdNoteAlt,
  MdPinDrop,
  MdDashboard,
  MdMyLocation,
  MdAccountTree,
  MdLocalShipping,
  MdSettings,
  MdAttachMoney,
  MdArrowBack,
  MdArrowForward,
  MdMultipleStop,
} from 'react-icons/md';

import { WashHubSelector } from './WashHubSelector';

import { SideBarProps } from './SideBar.props';
import { useLocation } from 'react-router-dom';

const iconProps = {
  size: '22px',
  color: '#B3B3B3',
};

const EXPANDED_WIDTH = '225px';
const COLLAPSED_WIDTH = COLLAPSED_RAIL_WIDTH;

// Below this the sidebar collapses itself, so narrow windows keep their room
// for the content beside it.
const AUTO_COLLAPSE_BREAKPOINT = 1100;

type SubItemType = {
  title: string;
  path: string;
};

type ItemType = {
  title: string;
  icon: (props: any) => JSX.Element;
  path?: string;
  subItems?: SubItemType[];
};

const navItems: ItemType[] = [
  {
    title: 'Dashboard',
    icon: (props: any) => <MdDashboard {...iconProps} {...props} />,
    path: routesSettings.PORTAL_DASHBOARD.path,
  },
  {
    title: 'Hub Operations',
    icon: (props: any) => <MdLocalShipping {...iconProps} {...props} />,
    subItems: [
      // {
      //   title: 'Open Items',
      //   path: routesSettings.PORTAL_OPEN_ITEMS.path,
      // },
      {
        title: 'Orders',
        path: routesSettings.PORTAL_ORDERS.path,
      },
      // {
      //   title: 'Orders Summary',
      //   path: routesSettings.PORTAL_ORDERS_SUMMARY.path,
      // },
      // {
      //   title: 'Deliveries',
      //   path: routesSettings.PORTAL_DELIVERIES.path,
      // },
      {
        title: 'Pickups',
        path: routesSettings.PORTAL_PICKUPS.path,
      },
      {
        title: 'Requests',
        path: routesSettings.PORTAL_REQUESTS.path,
      },
      {
        title: 'QA Log',
        path: routesSettings.PORTAL_QA_LOG.path,
      },
      // 'Transfers' is inserted here when a wash hub is selected — see
      // navItemsForHub in SideBarView.
      {
        // Kept last in this tab by request; navItemsForHub preserves that.
        title: 'Wash Hubs',
        path: routesSettings.PORTAL_WASH_HUB.path,
      },
    ],
  },
  {
    title: 'Customers',
    icon: (props: any) => <MdPinDrop {...iconProps} {...props} />,
    subItems: [
      {
        title: 'Customer Sites',
        path: routesSettings.PORTAL_CUSTOMER_SITES.path,
      },
      {
        title: 'Collection Points',
        path: routesSettings.PORTAL_COLLECTION_POINTS.path,
      },
      {
        title: 'Customer Contacts',
        path: routesSettings.PORTAL_CUSTOMER_CONTACTS.path,
      },
      {
        title: 'Groups',
        path: routesSettings.PORTAL_GROUPS.path,
      },
    ],
  },
  {
    title: 'Inventory',
    icon: (props: any) => <MdAccountTree {...iconProps} {...props} />,
    subItems: [
      {
        title: "SKU's",
        path: routesSettings.PORTAL_SKU.path,
      },
      {
        title: 'Unit Tracking',
        path: routesSettings.PORTAL_UNIT_TRACKING.path,
      },
      {
        title: 'Inventory Summary',
        path: routesSettings.PORTAL_ITEMS_SUMMARY.path,
      },
      {
        title: 'Hub Counts',
        path: routesSettings.PORTAL_WASHUB_INVENTORY.path,
      },
      {
        title: 'Manufacturers',
        path: routesSettings.PORTAL_MANUFACTURERS.path,
      },
    ],
  },
  {
    title: 'Tracking',
    icon: (props: any) => <MdMyLocation {...iconProps} {...props} />,
    subItems: [
      {
        title: 'Scans',
        path: routesSettings.PORTAL_SCANS.path,
      },
      // {
      //   title: 'Scans Types',
      //   path: routesSettings.PORTAL_SCANS_TYPES.path,
      // },
      {
        title: 'History',
        path: routesSettings.PORTAL_HISTORY.path,
      },
    ],
  },
  {
    // Customer Contacts and Groups moved to 'Customers', leaving only the
    // employees list — so this is a direct link rather than a tab wrapping a
    // single identically-named sub-item.
    title: 'Employees',
    icon: (props: any) => <MdPeople {...iconProps} {...props} />,
    path: routesSettings.PORTAL_EMPLOYEES.path,
  },
  {
    title: 'Reports',
    icon: (props: any) => <MdNoteAlt {...iconProps} {...props} />,
    subItems: [
      {
        title: 'Inventory Count',
        path: routesSettings.PORTAL_INVENTORY_COUNT.path,
      },
      {
        title: 'Harvest Summary',
        path: routesSettings.PORTAL_HARVEST_SUMMARY.path,
      },
      {
        title: 'Impact Report',
        path: routesSettings.PORTAL_IMPACT_REPORT.path,
      },
    ],
  },
  {
    title: 'Accounting',
    icon: (props: any) => <MdAttachMoney {...iconProps} {...props} />,
    subItems: [
      {
        title: 'Purchase Orders',
        path: routesSettings.PORTAL_PURCHASE_ORDERS.path,
      },
    ],
  },
  {
    title: 'Transfers',
    icon: (props: any) => <MdMultipleStop {...iconProps} {...props} />,
    path: routesSettings.PORTAL_TRANSFERS.path,
  },
];

const SideBarView: FC<SideBarProps> = (_) => {
  const { pathname } = useLocation();

  const [openNavItemTitle, setOpenNavItemTitle] = useState(pathname);

  // Which rail item currently owns the hover flyout. Held here rather than per
  // item so that moving between icons swaps the panel at once: the item being
  // entered claims it, which drops the previous one without waiting out its
  // close delay. Without this the two overlap for the length of that delay.
  const [flyoutTitle, setFlyoutTitle] = useState<string | null>(null);
  const isSuperAdmin = useIsSuperAdmin();
  const roles = useRoles();
  const canViewAccounting = Boolean(
    roles?.some(
      (permission) =>
        permission.role === RolesEnum.WASH_HUB_ADMIN ||
        permission.role === RolesEnum.SUPER_ADMIN,
    ),
  );

  const isNarrow = () => window.innerWidth < AUTO_COLLAPSE_BREAKPOINT;

  const [isCollapsed, setIsCollapsed] = useState(isNarrow);

  // Collapse when the window becomes narrow and restore when it widens again,
  // but only as the breakpoint is *crossed* — otherwise every resize event
  // would overrule a collapse or expand the user just chose deliberately.
  useEffect(() => {
    let wasNarrow = isNarrow();

    const onResize = () => {
      const narrow = isNarrow();

      if (narrow !== wasNarrow) {
        wasNarrow = narrow;
        setIsCollapsed(narrow);
      }
    };

    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onToggle = (selectedTitle: string) => {
    setOpenNavItemTitle(selectedTitle);
  };

  const selectedWashHubId = washHub.selectors.useSelectedWashHubId();

  // Transfers only applies to a specific hub, so it appears under Hub
  // Operations once one is selected. Derived rather than mutating the shared
  // navItems array, which previously left the entry behind for other consumers.
  // Inserted above 'Wash Hubs' so that entry stays at the bottom of the tab.
  const navItemsForHub = useMemo(
    () =>
      navItems.map((item) => {
        if (item.title !== 'Hub Operations' || selectedWashHubId === '') {
          return item;
        }

        const subItems = item.subItems || [];
        const washHubsIndex = subItems.findIndex(
          (subItem) => subItem.title === 'Wash Hubs',
        );
        const insertAt = washHubsIndex === -1 ? subItems.length : washHubsIndex;

        return {
          ...item,
          subItems: [
            ...subItems.slice(0, insertAt),
            {
              title: 'Transfers',
              path: routesSettings.PORTAL_TRANSFERS.path,
            },
            ...subItems.slice(insertAt),
          ],
        };
      }),
    [selectedWashHubId],
  );

  const isStaging = process.env.REACT_APP_ENVIRONMENT === 'staging';
  const isDev = process.env.REACT_APP_ENVIRONMENT === 'dev';

  // System section for SUPER_ADMIN only
  const systemNavItem: ItemType = {
    title: 'System',
    icon: (props: any) => <MdSettings {...iconProps} {...props} />,
    subItems: [
      {
        title: 'BACKGROUND PROCESSES',
        path: routesSettings.PORTAL_WORKERS.path,
      },
    ],
  };

  const roleFilteredNavItems = canViewAccounting
    ? navItemsForHub
    : navItemsForHub.filter((item) => item.title !== 'Accounting');

  const displayNavItems = isSuperAdmin
    ? [...roleFilteredNavItems, systemNavItem]
    : roleFilteredNavItems;

  return (
    <Flex
      pos="relative"
      h="100vh"
      overflow="hidden"
      transition="width 0.2s ease"
      w={isCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH}>
      {/* Painted above the page content: the hover flyouts escape this element
          and would otherwise be covered by anything later in the document,
          since their own z-index only competes inside this container. */}
      <Flex
        pt="20px"
        pos="fixed"
        zIndex={1300}
        h="100vh"
        top={0}
        bottom={0}
        bg="#F5F5F5"
        border="1px solid rgba(0,0,0,0.1)"
        flexDir="column"
        overflowY="auto"
        css={{
          '::-webkit-scrollbar': {
            display: 'none',
          },
        }}
        justifyContent="space-between"
        transition="width 0.2s ease"
        w={isCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH}>
        <Flex
          p="10px"
          flexDir="column"
          w="100%"
          as="nav"
          alignItems={'flex-start'}>
          <Flex w="100%" mb="10px" align="flex-start" justify="space-between">
            <Center w="100%" position="relative">
              {/* The wide lockup cannot shrink into the rail legibly, so the
                  round mark stands in for it there. */}
              <Image
                src={isCollapsed ? roundLogo : logo}
                h={isCollapsed ? '40px' : '78px'}
                maxW={isCollapsed ? '40px' : '182px'}
                objectFit="contain"
              />
              {/* Abbreviated in the rail, where the full word overruns the
                  sidebar and is clipped. */}
              {(isStaging || isDev) && (
                <Text
                  px={1}
                  fontSize="10px"
                  fontWeight="bold"
                  color="support.100"
                  bg="primary.500"
                  borderRadius="3px"
                  top="-10px"
                  right={0}
                  position="absolute"
                  title={isStaging ? 'Staging' : 'Development'}>
                  {isCollapsed
                    ? isStaging
                      ? 'STG'
                      : 'DEV'
                    : isStaging
                    ? 'Staging'
                    : 'Development'}
                </Text>
              )}
            </Center>
          </Flex>
          <WashHubSelector isCollapsed={isCollapsed} />
          {/* Secondary/outline button: bordered, muted, and quiet next to the
              nav items, so the control reads as chrome rather than a
              destination. */}
          <Flex
            as="button"
            type="button"
            my="8px"
            px={isCollapsed ? 0 : 3}
            h="36px"
            w="100%"
            gap={2}
            fontSize="14px"
            fontWeight="500"
            borderRadius="8px"
            border="1px solid"
            borderColor="blackAlpha.200"
            bg="transparent"
            color="support.400"
            align="center"
            justify={isCollapsed ? 'center' : 'flex-start'}
            transition="background-color 0.15s ease, color 0.15s ease"
            _hover={{ bg: 'blackAlpha.50', color: 'support.500' }}
            _active={{ bg: 'blackAlpha.100' }}
            _focusVisible={{
              outline: '2px solid',
              outlineColor: 'primary.500',
              outlineOffset: '2px',
            }}
            title={isCollapsed ? 'Expand Menu' : undefined}
            aria-label={isCollapsed ? 'Expand Menu' : 'Collapse Menu'}
            aria-expanded={!isCollapsed}
            onClick={() => setIsCollapsed((collapsed) => !collapsed)}>
            {isCollapsed ? (
              <MdArrowForward size="18px" />
            ) : (
              <>
                <MdArrowBack size="18px" />
                <Text userSelect="none">Collapse Menu</Text>
              </>
            )}
          </Flex>
          {displayNavItems.map((item, index) => (
            <NavItem
              key={`${item.title}-${index}`}
              icon={item.icon}
              title={item.title}
              subItems={item.subItems}
              path={item.path}
              toggle={item.title === openNavItemTitle}
              onToggle={onToggle}
              isCollapsed={isCollapsed}
              isFlyoutOpen={item.title === flyoutTitle}
              onFlyoutOpen={() => setFlyoutTitle(item.title)}
              onFlyoutClose={() =>
                setFlyoutTitle((current) =>
                  current === item.title ? null : current,
                )
              }
            />
          ))}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default SideBarView;
